import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import { DecorationField, Star } from "@/components/site/Decorations";
import { Button } from "@/components/ui/button";
import { ArrowRight, Compass } from "lucide-react";
import { IMG } from "@/lib/siteData";

const settings = [
  { title: "Special Classes 8:1:2", desc: "Small group support with a teacher and two paraprofessionals for up to 8 children." },
  { title: "Special Classes 12:1:2", desc: "Structured classroom with intensive supports for up to 12 children." },
  { title: "SCIS – Special Class Integrated Setting", desc: "Children with and without IEPs learning side by side." },
];

const services = [
  "Speech Therapy",
  "Occupational Therapy (OT)",
  "Physical Therapy (PT)",
  "Counselling & Play Therapy",
  "Parent Workshops & Events to reinforce learning at home",
];

export default function SpecialEdPage() {
  return (
    <div data-testid="special-ed-page" className="overflow-hidden">
      <SEO
        title="Preschool Special Education (4410)"
        description="Innovators Prep's NYS-approved 4410 Preschool Special Education Program — SCIS, 12:1:2, and 8:1:2 settings with integrated therapies and family support."
      />
      <PageHero
        title="Preschool Special Education Program"
        crumb="Preschool Special Education Program"
      />

      {/* Programs Offered */}
      <section className="relative py-20 md:py-28">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-5 rounded-[3rem] bg-brand-purple-soft -rotate-2" />
              <img
                src={IMG.specialEdPrograms}
                alt="Innovators Prep preschool special education classroom"
                className="relative rounded-[2.5rem] w-full aspect-[5/4] object-cover shadow-xl"
              />
              <div className="absolute -top-4 -right-4">
                <Star color="#FBBF24" size={40} />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-purple-soft text-brand-purple-deep text-xs font-bold tracking-widest uppercase">
              Programs Offered
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Specialised Settings & <span className="text-brand-purple">Family Support</span>
            </h2>

            <h3 className="mt-7 font-display text-xl font-semibold text-gray-900">
              Special Education Class Settings
            </h3>
            <div className="mt-3 space-y-3">
              {settings.map((s) => (
                <div key={s.title} className="bg-brand-green-soft/50 rounded-2xl p-4">
                  <div className="font-semibold text-brand-green-deep">{s.title}</div>
                  <div className="text-sm text-gray-600 mt-1">{s.desc}</div>
                </div>
              ))}
            </div>

            <h3 className="mt-7 font-display text-xl font-semibold text-gray-900">
              Related Services & Family Support
            </h3>
            <ul className="mt-3 grid sm:grid-cols-2 gap-2">
              {services.map((s) => (
                <li key={s} className="flex items-center gap-2 text-gray-700">
                  <span className="h-2 w-2 rounded-full bg-brand-purple" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Our Approach teaser */}
      <section className="relative py-16 md:py-20 bg-brand-cream">
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="bg-white rounded-[2.5rem] shadow-md border border-gray-100 p-8 md:p-12 flex flex-col md:flex-row gap-8 items-center text-center md:text-left">
              <div className="h-20 w-20 shrink-0 rounded-3xl bg-gradient-to-br from-brand-purple to-brand-green text-white flex items-center justify-center shadow-md">
                <Compass className="h-9 w-9" />
              </div>
              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
                  Our Approach
                </span>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-gray-900">
                  How we support every learner
                </h3>
                <p className="mt-3 text-gray-600 leading-relaxed">
                  Individualized instruction, a collaborative team, deep family engagement, and warm
                  social-emotional support — the four pillars that bring every IEP goal to life.
                </p>
              </div>
              <Button
                asChild
                data-testid="psed-approach-cta"
                className="bg-brand-purple hover:bg-brand-purple-deep text-white rounded-full px-7 py-6 font-semibold shrink-0"
              >
                <Link to="/approach">See Our Approach <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
